import { useState } from "react";
import { Form } from "react-router-dom";
import ModalNew from "../popup/Modal";
import PopupHeader from "../popup/common/popupHeader";
import UnifiedModal from "../popup/UnifiedModal";

function AvatarUploadModal({
  isOpen,
  onClose,
  avatarUrl,
  onUploadSuccess,
  onUploadError,
  uploadFunction,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    setSelectedFile(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async (event) => {
    event.preventDefault();

    if (!selectedFile || !uploadFunction) return;

    setIsUploading(true);

    try {
      const result = await uploadFunction(selectedFile);
      
      if (result.avatar || result.avatar_url) {
        // Clear state
        setSelectedFile(null);
        setPreviewUrl(null);
        
        // Call success callback
        if (onUploadSuccess) {
          onUploadSuccess(result.avatar || result.avatar_url);
        }
        
        // Close modal
        handleClose();
      } else {
        if (onUploadError) {
          onUploadError("Failed to upload avatar. Please try again.");
        }
      }
    } catch (error) {
      console.error("Avatar upload error:", error);
      if (onUploadError) {
        onUploadError("Failed to upload avatar. Please try again.");
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalNew className="content-modal">
      <PopupHeader
        title="Upload Avatar"
        setOpenState={(isOpen) => {
          if (!isOpen) {
            handleClose();
          }
        }}
      />
      
      <UnifiedModal>
        <Form onSubmit={handleUpload} className="avatar-upload-form" noValidate>
          <div className="modal-content">
            <div className="avatar-preview-section">
              <div className="avatar-preview">
                <img
                  src={
                    previewUrl ||
                    avatarUrl ||
                    `${process.env.PUBLIC_URL}/images/avatar_settings.svg`
                  }
                  alt="Avatar Preview"
                />
              </div>
            </div>

            <div className="file-input-section">
              <input
                type="file"
                name="avatar"
                id="avatar-upload"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <div
                className="drag-drop-area"
                onClick={(e) => {
                  e.preventDefault();
                  const fileInput = document.getElementById("avatar-upload");
                  if (fileInput) {
                    fileInput.click();
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add("drag-over");
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove("drag-over");
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("drag-over");
                  const files = e.dataTransfer.files;
                  if (files.length > 0) {
                    const file = files[0];
                    const event = { target: { files: [file] } };
                    handleFileSelect(event);
                  }
                }}
              >
                <div className="drag-drop-content">
                  <div className="drag-drop-icon">📁</div>
                  <p>Drag & drop an image here</p>
                  <p className="drag-drop-or">or</p>
                  <button
                    type="button"
                    className="cta-button secondary short text-center"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const fileInput = document.getElementById("avatar-upload");
                      if (fileInput) {
                        fileInput.click();
                      }
                    }}
                  >
                    Browse Files
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="cta-button secondary short text-center"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="cta-button primary short text-center"
              disabled={!selectedFile || isUploading}
            >
              {isUploading ? "Uploading..." : "Upload Avatar"}
            </button>
          </div>
        </Form>
      </UnifiedModal>
    </ModalNew>
  );
}

export default AvatarUploadModal;

